import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionCallout } from '@clarion/ClarionCallout';
import { ClarionCard } from '@clarion/ClarionCard';
import { ClarionCheckbox } from '@clarion/ClarionCheckbox';
import { ClarionDialog, ClarionDialogFooter } from '@clarion/ClarionDialog';
import { Icon } from '@clarion/ClarionIcon';
import { useEffect, useState } from 'react';
import supportLinks from '../shared/supportLinks';
import { AppToaster } from '../shared/toaster';

type EulaAcceptanceDialogProps = {
  isOpen: boolean;
  onAccept: () => Promise<void>;
  onReject: () => void;
};

export function EulaAcceptanceDialog({ isOpen, onAccept, onReject }: EulaAcceptanceDialogProps) {
  const [hasReviewd, setHasReviewed] = useState(false);
  const [hasAgreedToCompetitiveAnalysis, setHasAgreedToCompetitiveAnalysis] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedCheckbox, setHasAttemptedCheckbox] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setHasReviewed(false);
      setHasAgreedToCompetitiveAnalysis(false);
      setIsSubmitting(false);
      setHasAttemptedCheckbox(false);
    }
  }, [isOpen]);

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await onAccept();
    } catch (error) {
      console.error('Error accepting EULA:', error);
      setIsSubmitting(false);
    }
  };

  const canAccept = hasReviewd && hasAgreedToCompetitiveAnalysis;

  return (
    <ClarionDialog
      isOpen={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      title="End User License Agreement"
      isDismissable={false}
      maxWidth="500px"
    >
      <ClarionCallout intent="primary" className="mb-4">
        <p>
          As part of our commitment to SOC2 compliance and enterprise security requirements, we now
          require all users to formally accept our End User License Agreement (EULA) and Terms of
          Service. Please review the document by clicking the link below.
        </p>
        <p className="mt-2">
          If your organization has a contract with Recap Innovations, these EULA terms are the same
          as those that were already reviewed and signed off by your organization leadership or
          directors on behalf of your organization. Your organization may have also executed other
          agreements that supersede or ammend these terms.
        </p>
      </ClarionCallout>

      <ClarionCard className="mb-4">
        <a
          href={supportLinks.terms}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline font-medium flex items-center gap-3"
          onClick={() => {
            // Mark as reviewed when they click to open
            setTimeout(() => setHasReviewed(true), 1000);
          }}
        >
          View End User License Agreement and Terms of Service
          <Icon icon="share" />
        </a>
        {!hasReviewd && hasAttemptedCheckbox && (
          <ClarionCallout intent="warning" className="mt-4" id="eula-review-required-callout">
            Click the link above to review the agreement before you can continue.
          </ClarionCallout>
        )}
        {hasReviewd && (
          <div className="text-sm opacity-75 mt-2 flex items-center gap-2">
            <Icon icon="tick" intent="success" />
            Document opened. Select the acknowledgment checkbox, then choose Accept and Continue.
          </div>
        )}
      </ClarionCard>

      <ClarionCard>
        <h3 className="font-semibold mb-2">Competitive Analysis Prohibition</h3>
        <p className="text-sm mb-3 opacity-75">
          You agree not to use this platform or any of its features, services, or data for the
          purpose of competitive analysis, reverse engineering, or to develop competing products or
          services. This includes, but is not limited to, benchmarking our services against
          competing offerings, analyzing our pricing or feature sets for competitive purposes, or
          using our platform to inform the development of similar or competing products.
        </p>
        <ClarionCheckbox
          isSelected={hasAgreedToCompetitiveAnalysis}
          onChange={(isSelected) => {
            if (!hasReviewd) {
              setHasAttemptedCheckbox(true);
              AppToaster.show({
                message: 'Please review the agreement before accepting.',
                intent: 'warning',
              });
            } else {
              setHasAgreedToCompetitiveAnalysis(isSelected);
            }
          }}
          isInvalid={!hasReviewd && hasAttemptedCheckbox}
          isRequired
          aria-errormessage={
            !hasReviewd && hasAttemptedCheckbox ? 'eula-review-required-callout' : undefined
          }
        >
          I acknowledge and agree to the Terms of Service / End User License Agreement and
          competitive analysis prohibition stated above.
        </ClarionCheckbox>
      </ClarionCard>

      <ClarionDialogFooter>
        <ClarionButton onClick={onReject} isDisabled={isSubmitting}>
          Decline and Sign Out
        </ClarionButton>
        <ClarionButton
          intent="primary"
          onClick={handleAccept}
          isDisabled={!canAccept || isSubmitting}
          loading={isSubmitting}
          style={
            !canAccept || isSubmitting
              ? {
                  color: 'rgba(0, 0, 0, 0.7)',
                  backgroundColor: '#d1c4e9',
                }
              : undefined
          }
        >
          Accept and Continue
        </ClarionButton>
      </ClarionDialogFooter>
    </ClarionDialog>
  );
}
